<template>
  <div>
  <v-dialog
    v-model="dialog"
    :fullscreen="$vuetify.display.smAndDown"
    scrollable
  >
    <v-card>
      <v-card-title class="redColor details-dialog-title d-flex align-center ga-2">
        <div class="details-dialog-title-text text-white flex-grow-1">
          <span class="text-subtitle-1 font-weight-medium d-block">
            {{ $t("vehicle_entry.car_details") }}
          </span>
          <span
            v-if="vehicle?.brand?.title || vehicle?.model?.fullDescription"
            class="text-body-2 d-block"
          >
            <template v-if="vehicle?.brand?.title">{{
              vehicle.brand.title
            }}</template>
            <template v-if="vehicle?.model?.fullDescription">
              {{ " " + vehicle.model.fullDescription }}
            </template>
          </span>
        </div>
        <v-btn
          variant="outlined"
          icon
          color="white"
          class="details-dialog-close-btn flex-shrink-0"
          size="small"
          @click="close"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-card-text>
        <v-row class="pt-3">
          <!--------------------------------------------------------------------------------------->
          <v-col cols="12" md="12" lg="12">
            <!-- CAR DETAILS -->
            <v-row v-if="vehicle && vehicle.version" class="flex-wrap">
              <v-col cols="12" md="5">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.version")
                  }}</span>
                  <span class="font-weight-bold">{{
                    vehicle.version.description
                  }}</span>
                </v-row>
              </v-col>
              <v-col cols="5" sm="6" md="2">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.year")
                  }}</span>
                  <span class="font-weight-bold">
                    {{ formatMonthYear(vehicle.version.startingDate) }} -
                    {{ formatMonthYear(vehicle.version.endingDate) }}
                  </span>
                </v-row>
              </v-col>
              <v-col cols="3" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.body")
                  }}</span>
                  <span class="font-weight-bold">{{
                    vehicle.version.numberOfDoors
                  }}</span>
                </v-row>
              </v-col>
              <v-col cols="4" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.power")
                  }}</span>
                  <span class="font-weight-bold">
                    {{ vehicle.version.power.kw }} kw
                  </span>
                </v-row>
              </v-col>
              <v-col cols="6" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.engine")
                  }}</span>
                  <span class="font-weight-bold">{{
                    vehicle.version.engineCode
                  }}</span>
                </v-row>
              </v-col>
              <v-col cols="6" sm="6" md="auto">
                <v-row class="d-flex flex-column">
                  <span class="caption">{{
                    $t("parts.advanced_search.supply.supply")
                  }}</span>
                  <span class="font-weight-bold">{{
                    getSupplyLocale(vehicle.version.supplyCode)
                  }}</span>
                </v-row>
              </v-col>
            </v-row>
          </v-col>
          <!--------------------------------------------------------------------------------------->
          <v-col cols="12" md="6">
            <!-- VEHICLE DETAILS -->
            <vehicle-details ref="vehicleDetailsRef" :disabled="true" />
            <!-- PHOTO GRID -->
            <v-row v-if="vehicle && vehicle.photos" class="details-dialog-photos flex-wrap">
              <div
                v-for="(image, index) in vehicle.photos"
                :key="index"
                class="pa-1 mt-3"
              >
                <v-card
                  flat
                  tile
                  draggable="true"
                  :data-index="index"
                  @mouseenter="hoverIndex = index"
                  @mouseleave="hoverIndex = null"
                >
                  <v-img
                    :src="
                      image && image.thumb
                        ? `https://leko-tech.fra1.cdn.digitaloceanspaces.com/${image.thumb}`
                        : `https://portal.lekotech.com/logo_small.png`
                    "
                    width="150px"
                    class="mx-1"
                  >
                    <v-row
                      v-if="hoverIndex === index"
                      class="ma-1 d-flex justify-space-between"
                    >
                      <v-icon
                        size="x-large"
                        color="primary"
                        class="pointer"
                        @click="viewImage(image)"
                      >
                        mdi-image
                      </v-icon>
                    </v-row>
                  </v-img>
                </v-card>
              </div>
            </v-row>
          </v-col>
          <!--------------------------------------------------------------------------------------->
          <v-col cols="12" md="6">
            <!-- CAD stays mounted under v-show so position/parts dialogs keep working unchanged. -->
            <v-tabs
              v-model="partsViewTab"
              color="red-darken-3"
              slider-color="grey-darken-4"
              bg-color="grey-lighten-4"
              class="grafiche-step2-tabs"
            >
              <v-tab value="cad">
                {{ $t("vehicle_entry.step2_tab_cad") }}
              </v-tab>
              <v-tab value="grafiche">
                {{ $t("vehicle_entry.step2_tab_grafiche") }}
              </v-tab>
            </v-tabs>

            <div v-show="partsViewTab === 'cad'" class="mt-3">
              <v-row class="justify-center">
                <v-col cols="12" class="d-flex justify-center">
                  <vehicle-view
                    ref="vehicleView"
                    @position="openPartSelectionDialog"
                  />
                </v-col>
              </v-row>
            </div>
            <div v-show="partsViewTab === 'grafiche'">
              <infocar-grafiche-accordion
                v-if="vehicle"
                :vehicle="vehicle"
                :show-vehicle-summary="false"
                readonly
              />
            </div>
          </v-col>
          <!--------------------------------------------------------------------------------------->
          <v-col cols="12" md="12" lg="12">
            <div class="details-dialog-actions d-flex flex-wrap ga-2">
            <v-btn
              color="primary"
              variant="flat"
              class="mx-1"
              @click="openPartsList"
            >
              {{ $t("vehicle_entry.parts_list.label") }}
              <v-icon end> mdi-format-list-bulleted-triangle</v-icon>
            </v-btn>
            <v-btn
              color="primary"
              variant="flat"
              class="mx-1"
              @click="openInventoryList"
            >
              {{ $t("vehicle_entry.inventory_list.label") }}
              <v-icon end>mdi-format-list-bulleted</v-icon>
            </v-btn>
            <v-btn
              v-if="vehicle && vehicle.video"
              color="primary"
              class="mx-1"
              variant="flat"
              @click="viewVideo"
            >
              {{ $t("vehicle_entry.videos") }}
              <v-icon end> mdi-video-outline </v-icon>
            </v-btn>
            <v-btn
              v-if="vehicle && vehicle.document"
              icon
              variant="flat"
              class="mx-1"
              @click="viewDocument"
            >
              <v-icon end>mdi-file-document-outline</v-icon>
            </v-btn>
            </div>
          </v-col>
          <!--------------------------------------------------------------------------------------->
        </v-row>
      </v-card-text>
    </v-card>
  </v-dialog>
  <!-- HIDDEN COMPONENTS (outside parent dialog to avoid mobile overlay freeze) -->
  <selected-parts ref="selectedPartsRef" @close="showDialog" />
  <img-dialog ref="imgDialog" @close="showDialog" />
  <video-dialog ref="videoDialog" @close="showDialog" />
  <inventory-list
    ref="inventoryList"
    @go-to-inventory="goToInventory"
    @close="showDialog"
  />
  </div>
</template>

<script>
import moment from "moment";

import VehicleDetails from "@/components/VehicleEntry/vehicleDetails.vue";
import VehicleView from "@/components/VehicleEntry/vehicleView.vue";
import InfocarGraficheAccordion from "@/components/VehicleEntry/infocarGraficheAccordion.vue";

import ImgDialog from "@/components/imgDialog.vue";
import VideoDialog from "@/components/VehicleEntry/videoDialog.vue";

import SelectedParts from "@/components/VehicleEntry/selectedParts.vue";
import InventoryList from "@/components/VehicleEntry/inventoryList.vue";

export default {
  components: {
    "vehicle-details": VehicleDetails,
    "vehicle-view": VehicleView,
    "infocar-grafiche-accordion": InfocarGraficheAccordion,
    "selected-parts": SelectedParts,
    "inventory-list": InventoryList,
    "img-dialog": ImgDialog,
    "video-dialog": VideoDialog,
  },
  filters: {
    moment(date) {
      moment.locale(localStorage.getItem("language"));
      const valid = moment(date).isValid();
      if (valid) return moment(date).format("MM/YYYY");
      return "";
    },
  },
  data: () => ({
    dialog: false,
    vehicle: null,
    photos: null,
    hoverIndex: null,
    partsViewTab: "cad",
  }),
  computed: {
    server() {
      return this.$serverUri;
    },
  },
  methods: {
    ensureVehicleShapeForDialogs(vehicle) {
      if (!vehicle) return;
      if (!vehicle.parts || typeof vehicle.parts !== "object")
        vehicle.parts = {};
      if (
        vehicle.brand &&
        vehicle.model &&
        vehicle.version &&
        !vehicle.details?.brand
      ) {
        vehicle.details = {
          brand: vehicle.brand,
          model: vehicle.model,
          version: vehicle.version,
        };
      }
    },
    async open(vehicle) {
      this.partsViewTab = "cad";
      this.ensureVehicleShapeForDialogs(vehicle);
      this.vehicle = vehicle;
      this.$nextTick(() => {
        this.setVehicleDetails(vehicle);
      });
      this.dialog = true;
    },
    async close() {
      this.clearVehicleDetails();
      this.dialog = false;
      this.version = null;
    },
    setVehicleDetails(details) {
      this.$nextTick(() => {
        const vehicleDetails = this.$refs.vehicleDetailsRef;
        if (vehicleDetails) {
          vehicleDetails.setDetails(details);
        }
      });
    },
    clearVehicleDetails() {
      this.$nextTick(() => {
        const vehicleDetails = this.$refs.vehicleDetailsRef;
        if (vehicleDetails && typeof vehicleDetails.clear === "function") {
          vehicleDetails.clear();
        }
      });
    },
    viewImage(image) {
      const { imgDialog } = this.$refs;
      imgDialog.open({ type: "string", data: image });
      // this.hideDialog();
    },
    viewVideo() {
      const { videoDialog } = this.$refs;
      videoDialog.open({ type: "string", data: this.vehicle.video });
      this.hideDialog();
    },
    openPartSelectionDialog(position) {
      this.$nextTick(() => {
        const selectedParts = this.$refs.selectedPartsRef;
        if (selectedParts) {
          selectedParts.open(
            position,
            this.vehicle.parts[position],
            this.vehicle.parts.comment,
          );
        }
      });
      // const { selectedParts } = this.$refs;
      // console.log
      // selectedParts.open(
      //   position,
      //   this.vehicle.parts[position],
      //   this.vehicle.parts.comment,
      // );
      // this.hideDialog();
    },
    openInventoryList() {
      const { inventoryList } = this.$refs;
      // this.hideDialog();
      inventoryList.open(this.vehicle);
    },
    openPartsList() {
      this.$emit("parts-list", {
        vehicle: this.vehicle,
        partsList: this.vehicle.partsList,
        component: "details",
      });
      this.hideDialog();
    },
    hideDialog() {
      this.dialog = false;
    },
    showDialog() {
      this.dialog = true;
    },
    getSupplyLocale(supply) {
      switch (supply) {
        case "B":
          return this.$t("parts.advanced_search.supply.petrol");
        case "D":
          return this.$t("parts.advanced_search.supply.diesel");
        case "G":
          return this.$t("parts.advanced_search.supply.gas");
        default:
          return supply;
      }
    },
    viewDocument() {
      window.open(`${this.$serverUri}/documents?q=${this.vehicle.document}`);
    },
    goToInventory(data) {
      this.$emit("goToInventory", data);
      this.close();
    },
    formatMonthYear(dateString) {
      if (!dateString) return "";
      const date = new Date(dateString);
      const month = String(date.getUTCMonth() + 1).padStart(2, "0"); // months are 0-indexed
      const year = date.getUTCFullYear();
      return `${month}/${year}`;
    },
  },
};
</script>

<style scoped>
.details-dialog-title {
  padding: 12px 16px;
}

.details-dialog-title-text {
  min-width: 0;
  line-height: 1.35;
  word-break: break-word;
}

@media (max-width: 959.98px) {
  .details-dialog-title {
    position: relative;
    padding: 12px 52px 12px 16px;
  }

  .details-dialog-close-btn {
    position: absolute;
    top: 50%;
    right: 12px;
    transform: translateY(-50%);
  }

  .details-dialog-photos {
    justify-content: center;
  }

  .details-dialog-actions {
    justify-content: center;
  }

  .details-dialog-actions .v-btn {
    flex: 1 1 calc(50% - 8px);
    min-width: 0;
  }

  .details-dialog-actions .v-btn.v-btn--icon {
    flex: 0 0 auto;
  }
}
</style>
